import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { serviceRolesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// Bounded list — service-role catalogs are small (tens of rows); 500 matches
// the server cap and the same query the product picker uses, so it shares the
// cache.
const SERVICE_ROLES_LIST_QUERY = {
  sortField: 'name',
  sortDirection: 'asc',
  limit: 500,
} as const;

interface Props {
  /** Open when the user must choose which customer-team role to log under. */
  open: boolean;
  /** Candidate role ids — the user's roles on the customer that the chosen
   *  activity is loggable under (intersection computed server-side; ids arrive
   *  in the ROLE_REQUIRED field error's `constraint.candidateRoleIds`). */
  candidateRoleIds: readonly string[];
  /** Called with the picked role id; the caller retries the save with it as
   *  `customerTeamServiceRoleId`. */
  onPick: (roleId: string) => void;
  /** Called when the user dismisses without choosing — the save is abandoned. */
  onCancel: () => void;
}

/**
 * Disambiguation prompt shown only when an activity is loggable under MORE
 * THAN ONE of the user's roles on a customer (a user may serve several roles
 * on one customer). The chosen customer-team role drives approval routing and
 * is recorded on the entry, so it can't be guessed — the user picks. In the
 * common case (an activity maps to a single held role) the server auto-picks
 * silently and this never appears.
 */
export function RoleDisambiguationDialog({ open, candidateRoleIds, onPick, onCancel }: Props) {
  const roles = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
    enabled: open,
  });

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of roles.data?.data ?? []) map.set(r.id, r.name);
    return map;
  }, [roles.data]);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select a customer-team role</DialogTitle>
          <DialogDescription>
            You serve more than one role on this customer that can log this activity. Pick the role
            this time entry should be recorded under — it determines billing.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 py-2">
          {candidateRoleIds.map((id) => (
            <Button key={id} variant="outline" className="justify-start" onClick={() => onPick(id)}>
              {nameById.get(id) ?? id}
            </Button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
